import { Box, Chip, IconButton, Tooltip, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { formatCrmDateTime } from '@layouts/pages/CRM/kanban-board/formatters/crm-datetime';
import { WebhookSubscription } from '../services/webhook.service';
import { WEBHOOK_EVENTS } from '../constants/webhook-events';
import {
    statusPillSx, listCardSx, tagChipSx, monoLineSx,
} from './integration-list.styles';

interface Props {
    item: WebhookSubscription;
    onEdit: () => void;
    onDelete: () => void;
}

/// Um endereço cadastrado: para onde vai, o que avisa e como foi a última entrega.
export const WebhookRow = ({ item, onEdit, onDelete }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    /// Verde só quando a última tentativa voltou 2xx; sem tentativa é desconhecido, não entregue.
    const ok = item.lastStatusCode !== null
        && item.lastStatusCode !== undefined
        && item.lastStatusCode >= 200
        && item.lastStatusCode < 300;

    const deliveryColor = !item.lastAttemptAt ? cc.textMuted : ok ? cc.eventSuccess : cc.eventDanger;

    return (
        <Box sx={listCardSx(cc, item.active)}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: cc.textPrimary }} noWrap>
                        {item.name}
                    </Typography>
                    {!item.active && (
                        <Chip size="small" label={t('webhooks.pausado')} sx={tagChipSx(cc)} />
                    )}
                </Box>

                <Typography sx={monoLineSx(cc)} title={item.url}>
                    {item.url}
                </Typography>

                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                    {WEBHOOK_EVENTS.filter((event) => item.events.includes(event.value)).map((event) => (
                        <Chip
                            key={event.value}
                            size="small"
                            label={t(event.key)}
                            sx={tagChipSx(cc)}
                        />
                    ))}
                </Box>
            </Box>

            <Tooltip title={item.lastAttemptAt && !ok ? item.lastError ?? '' : ''}>
                <Box sx={statusPillSx(cc, deliveryColor)}>
                    {!item.lastAttemptAt
                        ? t('webhooks.semEnvios')
                        : `${ok ? t('webhooks.entregou') : t('webhooks.falhou')} · ${formatCrmDateTime(item.lastAttemptAt)}`}
                </Box>
            </Tooltip>

            <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                <Tooltip title={t('general.editar')}>
                    <IconButton size="small" onClick={onEdit} sx={{ color: cc.textSecondary }}>
                        <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('general.excluir')}>
                    <IconButton size="small" onClick={onDelete} sx={{ color: cc.eventDanger }}>
                        <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};
